import React, { useState, useEffect } from 'react';
import { getConfig, saveConfig } from '../api/api';
import {
    Box, Typography, Paper, Grid, Button, TextField, Select, MenuItem,
    Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
    Alert, IconButton, CircularProgress, FormControl, InputLabel, Chip,
    Tooltip
} from '@mui/material';
import {
    Save as SaveIcon,
    Settings as CogsIcon,
    Add as AddIcon,
    Delete as DeleteIcon,
    TableChart as TableIcon,
    ListAlt as ListAltIcon,
    Warning as WarningIcon
} from '@mui/icons-material';

const ALLOCATION_TYPES_KEY = 'allocation_types_config';
const BASES_CONFIG_KEY = 'dynamic_bases_config';

// Tipos de alocação Padrão (não editáveis, baseados em critérios internos)
const PADRAO_TYPES = [
    { id: 'direto', name: 'Alocação Direta', description: 'Custo atribuído diretamente (sem rateio).', isStandard: true, baseId: null },
    { id: 'step_down', name: 'Step-Down', description: 'Rateio sequencial entre Centros de Custo (Custos Indiretos).', isStandard: true, baseId: null },
    { id: 'receita_total', name: 'Receita Total', description: 'Rateio proporcional à Receita gerada pelo CC.', isStandard: true, baseId: null },
    { id: 'custo_total', name: 'Custo Total', description: 'Rateio proporcional ao Custo Próprio do CC.', isStandard: true, baseId: null },
    { id: 'producao', name: 'Produção', description: 'Rateio proporcional à Produção/Serviços entregues.', isStandard: true, baseId: null },
];

const TipoAlocacaoPage = () => {
    // Tipos de Alocação (Padrão + Dinâmicos)
    const [allocationTypes, setAllocationTypes] = useState([]);
    // Bases Auxiliares (para vincular)
    const [availableBases, setAvailableBases] = useState([]);

    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [status, setStatus] = useState('');
    const [error, setError] = useState(null);

    // Estado para o formulário de Cadastro de Novo Tipo
    const [newTypeForm, setNewTypeForm] = useState({
        id: '',
        name: '',
        baseId: '', // ID da base auxiliar a ser vinculada (opcional)
    });

    useEffect(() => {
        fetchAllData();
    }, []);

    const fetchAllData = async () => {
        setLoading(true);
        setError(null);
        try {
            // 1. Carrega as Bases Auxiliares Dinâmicas
            const basesRes = await getConfig(BASES_CONFIG_KEY);
            setAvailableBases(basesRes.data || []);

            // 2. Carrega os Tipos de Alocação Dinâmicos
            const typesRes = await getConfig(ALLOCATION_TYPES_KEY);
            const dynamicTypes = typesRes.data || [];

            // Combina tipos Padrão com tipos Dinâmicos salvos
            const allTypes = [...PADRAO_TYPES, ...dynamicTypes];
            setAllocationTypes(allTypes);

        } catch (err) {
            console.error("Erro ao carregar configurações:", err);
            setError('Falha ao carregar Tipos de Alocação e Bases Auxiliares.');
        } finally {
            setLoading(false);
        }
    };

    // Constrói a descrição para exibição na tabela
    const buildDescription = (type) => {
        if (type.isStandard) return type.description;

        const base = availableBases.find(b => b.id === type.baseId);

        if (base) {
            return (
                <Box sx={{ display: 'flex', alignItems: 'center', color: 'primary.main' }}>
                    <TableIcon sx={{ mr: 1, fontSize: 18 }} /> Base Dinâmica: {base.name} ({base.valueKey})
                </Box>
            );
        }
        return "Tipo de alocação personalizado sem base vinculada.";
    };

    const handleRegisterType = async () => {
        const { id, name, baseId } = newTypeForm;
        const normalizedId = id.trim().toLowerCase().replace(/\s+/g, '_').replace(/[^\w]/g, '');

        if (!normalizedId || !name.trim()) {
            setError("ID e Nome do Tipo de Alocação são obrigatórios.");
            return;
        }

        // Verifica se o ID já existe (entre padrões e dinâmicos)
        if (allocationTypes.some(t => t.id === normalizedId)) {
            setError(`O ID de Tipo de Alocação '${normalizedId}' já existe.`);
            return;
        }

        const newTypeEntry = {
            id: normalizedId,
            name: name.trim(),
            description: '', // Descrição será gerada com base na baseId
            isStandard: false,
            baseId: baseId || null,
        };

        // Pega apenas os tipos dinâmicos para salvar
        const dynamicTypes = allocationTypes.filter(t => !t.isStandard);
        const updatedDynamicTypes = [...dynamicTypes, newTypeEntry];

        setSaving(true);

        try {
            // Salva a nova lista de tipos dinâmicos configurados no DB
            await saveConfig(ALLOCATION_TYPES_KEY, updatedDynamicTypes);

            // Recarrega todos os dados (padrão + dinâmicos)
            await fetchAllData();

            setNewTypeForm({ id: '', name: '', baseId: '' });
            setStatus(`Novo tipo de alocação '${name}' registrado com sucesso!`);
        } catch (err) {
            setError(`Falha ao salvar o novo Tipo de Alocação: ${err.message}`);
        } finally {
            setSaving(false);
            setTimeout(() => setStatus(''), 3000);
        }
    };

    const handleDeleteType = async (typeId) => {
        if (!window.confirm(`Tem certeza que deseja deletar o Tipo de Alocação: ${typeId}? Esta ação não pode ser desfeita.`)) {
            return;
        }

        const dynamicTypes = allocationTypes.filter(t => !t.isStandard);
        const updatedDynamicTypes = dynamicTypes.filter(t => t.id !== typeId);

        setSaving(true);

        try {
            // Salva a lista dinâmica atualizada no DB
            await saveConfig(ALLOCATION_TYPES_KEY, updatedDynamicTypes);

            // Recarrega todos os tipos
            await fetchAllData();

            setStatus(`Tipo de Alocação '${typeId}' removido com sucesso.`);

        } catch (err) {
            setError(`Falha ao remover o Tipo de Alocação: ${err.message}`);
        } finally {
            setSaving(false);
            setTimeout(() => setStatus(''), 3000);
        }
    };

    const getBaseDisplay = (baseId) => {
        const base = availableBases.find(b => b.id === baseId);
        return base ? `${base.name} (${base.valueKey})` : '-- Base Inválida --';
    };

    if (loading) return (
        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh' }}>
            <CircularProgress />
        </Box>
    );

    return (
        <Box sx={{ p: 3 }}>
            <Typography variant="h4" gutterBottom sx={{ fontWeight: 'bold', color: 'text.primary', display: 'flex', alignItems: 'center' }}>
                <CogsIcon sx={{ mr: 2, color: 'primary.main' }} /> Cadastro de Tipos de Alocação
            </Typography>
            <Typography variant="body1" sx={{ mb: 4, color: 'text.secondary' }}>
                Defina os métodos que serão utilizados para ratear custos entre os centros de custo, vinculando-os a bases auxiliares ou critérios fixos.
            </Typography>

            {/* --- SEÇÃO DE CADASTRO DE NOVO TIPO --- */}
            <Paper sx={{ p: 3, mb: 4, borderLeft: 6, borderColor: 'warning.main' }}>
                <Typography variant="h6" gutterBottom sx={{ fontWeight: 'bold', display: 'flex', alignItems: 'center' }}>
                    <AddIcon sx={{ mr: 1, color: 'warning.main' }} /> Criar Novo Tipo de Alocação
                </Typography>
                <Grid container spacing={2} alignItems="center">
                    <Grid item xs={12} md={3}>
                        <TextField
                            fullWidth
                            label="ID Único (Chave)"
                            value={newTypeForm.id}
                            onChange={(e) => setNewTypeForm(prev => ({ ...prev, id: e.target.value.trim().toLowerCase().replace(/\s+/g, '_').replace(/[^\w]/g, '') }))}
                            placeholder="ex: numero_computadores"
                            size="small"
                        />
                    </Grid>
                    <Grid item xs={12} md={3}>
                        <TextField
                            fullWidth
                            label="Nome do Tipo"
                            value={newTypeForm.name}
                            onChange={(e) => setNewTypeForm(prev => ({ ...prev, name: e.target.value }))}
                            placeholder="ex: Proporção de PCs"
                            size="small"
                        />
                    </Grid>
                    <Grid item xs={12} md={3}>
                        <FormControl fullWidth size="small">
                            <InputLabel>Vincular a Base Auxiliar</InputLabel>
                            <Select
                                value={newTypeForm.baseId}
                                label="Vincular a Base Auxiliar"
                                onChange={(e) => setNewTypeForm(prev => ({ ...prev, baseId: e.target.value }))}
                            >
                                <MenuItem value=""><em>-- Selecione uma Base (Opcional) --</em></MenuItem>
                                {availableBases.map(base => (
                                    <MenuItem key={base.id} value={base.id}>
                                        {getBaseDisplay(base.id)}
                                    </MenuItem>
                                ))}
                            </Select>
                        </FormControl>
                    </Grid>
                    <Grid item xs={12} md={3}>
                        <Button
                            fullWidth
                            variant="contained"
                            color="primary"
                            onClick={handleRegisterType}
                            disabled={saving || !newTypeForm.id || !newTypeForm.name}
                            startIcon={saving ? <CircularProgress size={20} color="inherit" /> : <SaveIcon />}
                        >
                            {saving ? 'Salvando Tipo...' : 'Salvar Tipo de Alocação'}
                        </Button>
                    </Grid>
                </Grid>
            </Paper>

            {/* --- LISTAGEM DE TIPOS DE ALOCAÇÃO --- */}
            <Paper sx={{ p: 3 }}>
                <Typography variant="h6" gutterBottom sx={{ fontWeight: 'bold', display: 'flex', alignItems: 'center' }}>
                    <ListAltIcon sx={{ mr: 1, color: 'primary.main' }} /> Tipos de Alocação Cadastrados
                </Typography>
                <TableContainer sx={{ maxHeight: 500, border: 1, borderColor: 'divider', borderRadius: 1 }}>
                    <Table stickyHeader size="small">
                        <TableHead>
                            <TableRow>
                                <TableCell sx={{ fontWeight: 'bold', width: '20%' }}>ID / Chave</TableCell>
                                <TableCell sx={{ fontWeight: 'bold', width: '20%' }}>Nome</TableCell>
                                <TableCell sx={{ fontWeight: 'bold', width: '40%' }}>Descrição / Base Vinculada</TableCell>
                                <TableCell align="center" sx={{ fontWeight: 'bold', width: '20%' }}>Ações</TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {allocationTypes.map(type => (
                                <TableRow key={type.id} hover sx={type.isStandard ? { bgcolor: 'action.hover' } : {}}>
                                    <TableCell sx={{ fontFamily: 'monospace' }}>{type.id}</TableCell>
                                    <TableCell>{type.name}</TableCell>
                                    <TableCell sx={{ color: 'text.secondary' }}>
                                        {buildDescription(type)}
                                    </TableCell>
                                    <TableCell align="center">
                                        {type.isStandard ? (
                                            <Chip label="Padrão" color="primary" size="small" variant="outlined" />
                                        ) : (
                                            <Tooltip title="Deletar Tipo de Alocação Dinâmico">
                                                <IconButton
                                                    onClick={() => handleDeleteType(type.id)}
                                                    color="error"
                                                    size="small"
                                                >
                                                    <DeleteIcon />
                                                </IconButton>
                                            </Tooltip>
                                        )}
                                    </TableCell>
                                </TableRow>
                            ))}
                        </TableBody>
                    </Table>
                </TableContainer>
            </Paper>

            {status && (
                <Alert severity="success" sx={{ position: 'fixed', bottom: 16, right: 16, zIndex: 9999, boxShadow: 3 }}>
                    {status}
                </Alert>
            )}
            {error && (
                <Alert severity="error" sx={{ position: 'fixed', bottom: 16, right: 16, zIndex: 9999, boxShadow: 3 }}>
                    {error}
                </Alert>
            )}
        </Box>
    );
};

export default TipoAlocacaoPage;
